import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Modal from '../../../components/Modal/Modal';
import EmployeeNameSearch from '../../../components/EmployeeNameSearch/';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

// =============================================================================
// Estado inicial do formulário
// =============================================================================

const EMPTY_FORM_STATE = {
   order_number:              '',
   purchase_date:             '',
   product_description:       '',
   supplier_name:             '',
   total_purchase_amount:     '',
   total_installments_count:  '1',
   first_reference_month:     '',
   notes:                     '',
};

// =============================================================================
// Componente
// =============================================================================

/**
 * Modal para criação de compra em folha desacoplado do employeeId.
 * O funcionário é selecionado via autocomplete dentro do próprio modal.
 *
 * Props:
 *   isOpen    {boolean}  — controla visibilidade
 *   onClose   {fn}       — chamado ao fechar
 *   onSuccess {fn}       — chamado após criação bem-sucedida (para refetch da listagem)
 */
export default function NewPurchaseDeductionModal({ isOpen, onClose, onSuccess }) {
   const [selectedEmployee, setSelectedEmployee]       = useState(null);
   const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
   const [formData, setFormData]                       = useState(EMPTY_FORM_STATE);
   const [isSubmitting, setIsSubmitting]               = useState(false);

   // ─── Fechar e limpar estado ────────────────────────────────────
   const handleClose = useCallback(() => {
      setSelectedEmployee(null);
      setEmployeeSearchQuery('');
      setFormData(EMPTY_FORM_STATE);
      onClose();
   }, [onClose]);

   // ─── Handlers de campo ────────────────────────────────────────
   const handleFieldChange = (event) => {
      const { name, value } = event.target;
      setFormData((previous) => ({ ...previous, [name]: value }));
   };

   // ─── Submit ───────────────────────────────────────────────────
   const handleSubmit = async () => {
      if (!selectedEmployee) {
         toast.error('Selecione um funcionário.');
         return;
      }
      if (!formData.purchase_date) {
         toast.error('Data da compra é obrigatória.');
         return;
      }
      if (!formData.product_description.trim()) {
         toast.error('Descrição do produto é obrigatória.');
         return;
      }
      if (!formData.total_purchase_amount || Number(formData.total_purchase_amount) <= 0) {
         toast.error('Valor da compra deve ser maior que zero.');
         return;
      }
      if (!formData.total_installments_count || Number(formData.total_installments_count) < 1) {
         toast.error('Número de parcelas deve ser ao menos 1.');
         return;
      }
      if (!formData.first_reference_month) {
         toast.error('Mês de referência da 1ª parcela é obrigatório.');
         return;
      }

      setIsSubmitting(true);
      try {
         await axios.post(
            `/hr/employees/${selectedEmployee.id}/purchase-deductions`,
            {
               ...formData,
               total_purchase_amount:    Number(formData.total_purchase_amount),
               total_installments_count: Number(formData.total_installments_count),
               order_number:             formData.order_number || null,
               supplier_name:            formData.supplier_name || null,
               notes:                    formData.notes || null,
            },
         );

         toast.success(`Compra em folha registrada para ${selectedEmployee.full_name}!`);
         handleClose();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao registrar compra em folha.';
         toast.error(errorMessage);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Nova compra com desconto em folha"
         onClose={handleClose}
         maxWidth="600px"
      >
         <FormGrid $cols={12} $colsMobile={12}>

            {/* ─── Seleção de funcionário ──────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <label htmlFor="purchase-employee">Funcionário *</label>
               <EmployeeNameSearch
                  value={employeeSearchQuery}
                  onChange={setEmployeeSearchQuery}
                  onSelect={(employee) => {
                     setSelectedEmployee(employee);
                     setEmployeeSearchQuery(employee?.full_name ?? '');
                  }}
                  required
                  placeholder="Digite o nome do funcionário..."
                  showHint
               />
               {selectedEmployee && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                     {selectedEmployee.jobPosition?.position_name ?? ''}
                     {selectedEmployee.department?.department_name
                        ? ` · ${selectedEmployee.department.department_name}`
                        : ''}
                  </p>
               )}
            </Field>

            {/* ─── Campos da compra ────────────────────────────── */}
            <Field $col={6} $colMobile={12}>
               <label htmlFor="purchase-date">Data da compra *</label>
               <input
                  id="purchase-date"
                  type="date"
                  name="purchase_date"
                  value={formData.purchase_date}
                  onChange={handleFieldChange}
               />
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="purchase-order-number">Nº do pedido</label>
               <input
                  id="purchase-order-number"
                  type="text"
                  name="order_number"
                  value={formData.order_number}
                  onChange={handleFieldChange}
                  placeholder="Ex: OS 3240443"
               />
            </Field>

            <Field $col={12} $colMobile={12}>
               <label htmlFor="purchase-product-description">Descrição do produto *</label>
               <input
                  id="purchase-product-description"
                  type="text"
                  name="product_description"
                  value={formData.product_description}
                  onChange={handleFieldChange}
                  placeholder="Ex: Capacete, Uniforme, Ferramental..."
               />
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="purchase-supplier-name">Fornecedor</label>
               <input
                  id="purchase-supplier-name"
                  type="text"
                  name="supplier_name"
                  value={formData.supplier_name}
                  onChange={handleFieldChange}
                  placeholder="Nome do fornecedor..."
               />
            </Field>

            <Field $col={6} $colMobile={6}>
               <label htmlFor="purchase-total-amount">Valor total (R$) *</label>
               <input
                  id="purchase-total-amount"
                  type="number"
                  name="total_purchase_amount"
                  value={formData.total_purchase_amount}
                  onChange={handleFieldChange}
                  step="0.01"
                  min="0.01"
                  placeholder="0,00"
               />
            </Field>

            <Field $col={6} $colMobile={6}>
               <label htmlFor="purchase-installments-count">Nº de parcelas *</label>
               <input
                  id="purchase-installments-count"
                  type="number"
                  name="total_installments_count"
                  value={formData.total_installments_count}
                  onChange={handleFieldChange}
                  min="1"
                  step="1"
               />
            </Field>

            <Field $col={6} $colMobile={6}>
               <label htmlFor="purchase-first-reference-month">1º mês de desconto *</label>
               <input
                  id="purchase-first-reference-month"
                  type="month"
                  name="first_reference_month"
                  value={formData.first_reference_month}
                  onChange={handleFieldChange}
               />
            </Field>

            <Field $col={12} $colMobile={12}>
               <label htmlFor="purchase-notes">Observações</label>
               <input
                  id="purchase-notes"
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFieldChange}
                  placeholder="Observações opcionais..."
               />
            </Field>

            {/* ─── Ações ───────────────────────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
                  <button type="button" onClick={handleClose} disabled={isSubmitting}>
                     Cancelar
                  </button>
                  <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                     {isSubmitting ? 'Registrando...' : 'Registrar compra'}
                  </button>
               </div>
            </Field>

         </FormGrid>
      </Modal>
   );
}

NewPurchaseDeductionModal.propTypes = {
   isOpen:    PropTypes.bool.isRequired,
   onClose:   PropTypes.func.isRequired,
   onSuccess: PropTypes.func,
};
